import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Volume2, VolumeX, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Autoplay hero video that sits at the top of the homepage.
 * - Fetches the most recent admin's welcome video from a public endpoint.
 * - Hides itself completely if no video is uploaded yet.
 * - Autoplay is muted (browser policy). Users can tap the speaker icon to unmute.
 * - Dismissible via the X — remembers the choice for the session.
 */
export default function WelcomeVideoHero() {
  const [videoUrl, setVideoUrl] = useState(null);
  const [muted, setMuted] = useState(true);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && window.sessionStorage.getItem("welcome_video_dismissed") === "1",
  );
  const ref = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/api/welcome-video/public`)
      .then((r) => {
        if (r.data?.video_url) setVideoUrl(`${API}${r.data.video_url}`);
      })
      .catch(() => {});
  }, []);

  if (!videoUrl || dismissed) return null;

  const toggleMute = () => {
    if (!ref.current) return;
    ref.current.muted = !ref.current.muted;
    setMuted(ref.current.muted);
  };

  const dismiss = () => {
    try {
      window.sessionStorage.setItem("welcome_video_dismissed", "1");
    } catch {}
    setDismissed(true);
  };

  return (
    <section
      className="relative border-b border-border bg-black overflow-hidden"
      data-testid="home-welcome-video"
    >
      <div className="max-w-5xl mx-auto">
        <div className="relative aspect-video sm:aspect-[21/9] max-h-[70vh]">
          <video
            ref={ref}
            src={videoUrl}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            controls={false}
            data-testid="home-welcome-video-el"
          />
          {/* Subtle gradient so caption / buttons stay readable */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />

          {/* Caption + CTA overlay */}
          <div className="absolute bottom-6 left-0 right-0 px-6 sm:px-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="text-white max-w-xl">
              <div className="text-[10px] uppercase tracking-widest text-white/70 mb-2">A word from Di'Meka</div>
              <div className="font-serif text-xl sm:text-3xl leading-snug">
                Ready to enter the tax industry — or take your business to the next level?
              </div>
            </div>
            <button
              type="button"
              onClick={toggleMute}
              className="self-end sm:self-auto inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/15 border border-white/30 backdrop-blur-sm text-white text-sm hover:bg-white/25"
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="home-welcome-video-mute"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              <span className="hidden sm:inline">{muted ? "Tap to unmute" : "Mute"}</span>
            </button>
          </div>

          {/* Dismiss */}
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/50 hover:bg-black/70 text-white grid place-items-center"
            aria-label="Dismiss"
            data-testid="home-welcome-video-dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}
